import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, LogOut } from "lucide-react";
import { api } from "../lib/api";
import { useConfig, useWallet } from "../hooks/useHoodbank";
import { getWalletClient } from "../lib/wallet";

const buildSiweMessage = (domain, address, uri, nonce, chainId) => {
  const issuedAt = new Date().toISOString();
  const expiration = new Date(Date.now() + 10 * 60000).toISOString();
  return `${domain} wants you to sign in with your Ethereum account:\n${address}\n\nHoodBank Admin Access\n\nURI: ${uri}\nVersion: 1\nChain ID: ${chainId}\nNonce: ${nonce}\nIssued At: ${issuedAt}\nExpiration Time: ${expiration}`;
};

const Field = ({ label, children }) => (
  <div>
    <p className="font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase mb-1">{label}</p>
    {children}
  </div>
);

export default function Admin() {
  const config = useConfig();
  const { address, connect } = useWallet();
  const [session, setSession] = useState(null);
  const [overview, setOverview] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      const [o, a] = await Promise.all([api.get("/admin/overview"), api.get("/admin/audit?limit=25")]);
      setOverview(o.data);
      setAudit(a.data.logs);
    } catch {}
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      if (r.data.authenticated) { setSession(r.data.address); loadDashboard(); }
    }).catch(() => {});
  }, [loadDashboard]);

  const login = async () => {
    setBusy(true);
    try {
      const addr = address || (await connect());
      const { data: n } = await api.get("/auth/nonce");
      const message = buildSiweMessage(window.location.host, addr, window.location.origin, n.nonce, config?.chain_id || 4663);
      const client = getWalletClient(config || { chain_id: 4663, rpc_url: "", explorer_url: "" });
      const signature = await client.signMessage({ account: addr, message });
      await api.post("/auth/verify", { message, signature });
      setSession(addr.toLowerCase());
      toast.success("Signed in");
      loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.shortMessage || e.message);
    } finally { setBusy(false); }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setSession(null);
    setOverview(null);
  };

  const saveSetting = async (patch) => {
    try {
      await api.put("/admin/settings", patch);
      toast.success("Settings saved");
      loadDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  if (!session) {
    return (
      <div className="max-w-md mx-auto px-6 py-24" data-testid="admin-login-page">
        <div className="card-surface rounded-2xl p-10 border-t-2 border-t-[#00C805]">
          <ShieldCheck size={26} className="text-[#00C805] mb-5" />
          <h1 className="font-heading font-bold text-2xl tracking-tight">Admin access</h1>
          <p className="text-sm text-neutral-500 mt-3 leading-relaxed">
            Sign-In with Ethereum (EIP-4361). Only wallets whitelisted in <code className="text-neutral-300">ADMIN_WALLETS</code> may
            enter. Signing is gasless and proves wallet ownership only.
          </p>
          <button data-testid="siwe-login-btn" onClick={login} disabled={busy}
            className="mt-8 w-full rounded-full bg-[#00C805] text-black font-semibold px-6 py-3.5 text-sm hover:bg-[#13D059] transition-colors duration-200 disabled:opacity-50">
            {busy ? "Waiting for signature…" : "Sign-In with Ethereum"}
          </button>
        </div>
      </div>
    );
  }

  const s = overview?.settings || {};
  const Toggle = ({ k, label, testId }) => (
    <button data-testid={testId} onClick={() => saveSetting({ [k]: !s[k] })}
      className={`rounded-full px-5 py-2 text-xs font-mono font-semibold transition-colors duration-200 ${
        s[k] ? "bg-[#00C805] text-black" : "bg-[#171717] text-neutral-400 border border-white/10"}`}>
      {label}: {s[k] ? "ON" : "OFF"}
    </button>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="font-mono text-xs tracking-[0.2em] text-[#00C805] uppercase mb-2">Admin dashboard</p>
          <p className="font-mono text-sm text-neutral-400">{session}</p>
        </div>
        <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors duration-200">
          <LogOut size={15} /> Sign out
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="card-surface rounded-2xl p-8 lg:col-span-2">
          <h2 className="font-heading font-bold mb-6">Keeper & feature flags (off-chain)</h2>
          <div className="flex flex-wrap gap-3 mb-8">
            <Toggle k="keeper_enabled" label="Keeper" testId="toggle-keeper" />
            <Toggle k="auto_compound_relay" label="Auto-compound relay" testId="toggle-autocompound-relay" />
            <Toggle k="loyalty_multiplier" label="Loyalty multiplier" testId="toggle-loyalty" />
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Gas budget per cycle">
              <div className="flex gap-2">
                <input data-testid="input-gas-budget" defaultValue={s.gas_budget} id="gasb" type="number"
                  className="flex-1 bg-[#050505] border border-white/10 rounded-lg px-4 py-2.5 font-mono text-sm focus:outline-none focus:border-[#00C805]/60 transition-colors duration-200" />
                <button data-testid="save-gas-budget" onClick={() => saveSetting({ gas_budget: parseInt(document.getElementById("gasb").value) })}
                  className="rounded-lg bg-[#171717] border border-white/10 px-4 text-xs font-mono hover:border-[#00C805]/40 transition-colors duration-200">Save</button>
              </div>
            </Field>
            <Field label="Keeper spend cap (wei/day)">
              <div className="flex gap-2">
                <input data-testid="input-spend-cap" defaultValue={s.max_spend_wei_per_day} id="capw" type="number"
                  className="flex-1 bg-[#050505] border border-white/10 rounded-lg px-4 py-2.5 font-mono text-sm focus:outline-none focus:border-[#00C805]/60 transition-colors duration-200" />
                <button data-testid="save-spend-cap" onClick={() => saveSetting({ max_spend_wei_per_day: parseInt(document.getElementById("capw").value) })}
                  className="rounded-lg bg-[#171717] border border-white/10 px-4 text-xs font-mono hover:border-[#00C805]/40 transition-colors duration-200">Save</button>
              </div>
            </Field>
          </div>
          <p className="text-xs text-neutral-600 mt-6 leading-relaxed">
            On-chain parameters (dust threshold, fee %, pause, exclusions) require an owner-wallet
            transaction on the contracts — the backend can never bypass contract ownership. Use
            Blockscout's write panel or Remix with the owner wallet.
          </p>
        </div>

        <div className="space-y-6">
          <div className="card-surface rounded-2xl p-8">
            <h2 className="font-heading font-bold mb-4">Keeper status</h2>
            <div className="font-mono text-xs space-y-2 text-neutral-400" data-testid="keeper-status">
              <p>Configured: <span className={overview?.keeper_status?.configured ? "text-[#00C805]" : "text-amber-400"}>{String(overview?.keeper_status?.configured ?? false)}</span></p>
              <p>Enabled: {String(overview?.keeper_status?.enabled ?? false)}</p>
              <p>Wallet: {overview?.keeper_status?.wallet || "not set"}</p>
              <p>Last check: {overview?.keeper_status?.last_check?.slice(0, 19) || "—"}</p>
              <p>Indexer block: {overview?.indexer?.last_block ?? "—"}</p>
            </div>
          </div>
          <div className="card-surface rounded-2xl p-8">
            <h2 className="font-heading font-bold mb-4">On-chain</h2>
            <div className="font-mono text-xs space-y-2 text-neutral-400" data-testid="onchain-params">
              {overview?.configured ? (
                <>
                  <p>Fee bps: {overview.onchain?.fee_bps ?? "—"}</p>
                  <p>Dust threshold: {overview.onchain?.min_payout_wei ?? "—"} wei</p>
                  <p>Cycle: {overview.onchain?.cycle_length ?? "—"}s</p>
                  <p>Paused: {String(overview.onchain?.paused ?? "—")}</p>
                </>
              ) : (<p className="text-amber-400/80">Contracts not configured</p>)}
            </div>
          </div>
        </div>
      </div>

      <div className="card-surface rounded-2xl p-8 mt-6">
        <h2 className="font-heading font-bold mb-4">Audit log</h2>
        <div className="font-mono text-xs space-y-2 max-h-72 overflow-y-auto" data-testid="audit-log">
          {audit.map((l, i) => (
            <p key={i} className="text-neutral-500">
              <span className="text-neutral-300">{l.timestamp?.slice(0, 19)}</span> · {l.event}
              {l.address && <span className="text-[#00C805]"> · {l.address.slice(0, 10)}…</span>}
            </p>
          ))}
          {audit.length === 0 && <p className="text-neutral-600">No events yet</p>}
        </div>
      </div>
    </div>
  );
}
